import './shell.css';

import { ensureSignedIn, signOut } from './core/AccountGate';
import { enterDiagramRoom, enterLobbyRoom } from './net/api';
import { mountRoomRoute } from './session/mountRoomRoute';
import { showFatalErrorScreen } from './ui/FatalErrorScreen';

/**
 * The VR client's entry point, and the whole of it.
 *
 * **One destination, so no router.** `main.ts` needs one because the web app has six routes and
 * a back button that has to mean something; this application has exactly one place to be — a
 * room — and arriving anywhere else is not a thing it can do. What would have been routing here
 * is instead a straight line: make sure there is a session, get a room, mount it.
 *
 * **The splash is the entry point the owner asked for.** Capability checks, which hand,
 * standing or seated, and one button. There is no diagram picker on it, because the picker is
 * the lobby (SPEC-156) — the room you arrive in, showing your diagrams around you as live
 * miniatures — and you reach it by entering, not by choosing first. `mountRoomRoute` owns all
 * of that already; nothing about it needed changing for this document.
 *
 * **Nothing here signs anybody in for real.** `ensureSignedIn` shows the sign-in screen, whose
 * one control is a Google handshake that navigates away and lands back on the *web* host's
 * callback (`GoogleSignIn.CallbackPath`, a value registered in somebody else's console). That
 * is deliberate and is why this host issues no session of its own: one callback URL, on one
 * host, forever. What makes the returning cookie visible here is that it is scoped to the
 * parent domain rather than to one hostname — see the plan's cookie-domain note, which is the
 * single thing this whole split does not work without.
 */

/** Where the web app lives: the portal, the diagram list, account settings, sign-out. */
const WEB_ORIGIN = import.meta.env.VITE_TUNET_WEB_ORIGIN ?? window.location.origin;

const canvas = document.querySelector<HTMLCanvasElement>('#render');
const statusHost = document.querySelector<HTMLElement>('#status');
const splashHost = document.querySelector<HTMLElement>('#splash');
const sessionHost = document.querySelector<HTMLElement>('#session');
const hint = document.querySelector<HTMLElement>('#hint');
const notice = document.querySelector<HTMLElement>('#notice');
const reenterButton = document.querySelector<HTMLButtonElement>('#reenter');
const leaveRoomButton = document.querySelector<HTMLButtonElement>('#leave-room');

/**
 * Dev-only inspection handle. Same shape and same name as the web app's, so anything already
 * written against `window.tunet` in a headset browser's console keeps working — this is the
 * document that actually has a scene to poke at, and always was.
 */
const devHandle: Record<string, unknown> = {};

if (import.meta.env.DEV) {
  (window as unknown as { tunet: unknown }).tunet = devHandle;
}

let noticeTimer = 0;

function showNotice(message: string): void {
  if (!notice) {
    return;
  }

  notice.textContent = message;
  notice.hidden = false;

  window.clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => {
    notice.hidden = true;
  }, 2200);
}

function showFatalError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);

  if (splashHost) {
    showFatalErrorScreen(splashHost, message, () => void signOut());
  }

  console.error(error);
}

/**
 * `RoomRouteDeps.navigate`, for a host with nowhere of its own to navigate to.
 *
 * Every path the room session asks for — `/portal`, and the interim "no diagram, go and choose
 * one" fallback — is a *web app* path, and the web app is another origin now. So this is a real
 * page navigation rather than a router call, and the session it leaves is genuinely over: the
 * hub connection closes, the peer leaves the room, and coming back means entering again. That
 * is the honest behaviour for "take me to the portal" from inside a headset, and it is what
 * used to happen anyway — the old router navigation tore the whole room boot down and rebuilt
 * it, one route away.
 */
function leaveToWeb(path: string): void {
  window.location.assign(`${WEB_ORIGIN}${path}`);
}

async function start(): Promise<void> {
  if (!splashHost) {
    throw new Error('Page markup is missing #splash.');
  }

  // `WEB_ORIGIN`, not this document's own: the Google handshake has to start on the host that
  // owns the registered callback (see `SignIn`'s own remarks). While both documents are served
  // from one origin this is the same link it always was; it stops being so on the deploy that
  // gives the session its own host, and it is the one line that would otherwise fail there with
  // `redirect_uri_mismatch` and nothing to say why.
  const account = await ensureSignedIn(splashHost, WEB_ORIGIN);

  // `?diagram=` is how the portal's "Open in 3D" names one. Read before anything else touches
  // the URL, since the room id is written into it a few lines below.
  const requestedDiagramId = new URLSearchParams(window.location.search).get('diagram');

  // A ROOM IS SPAWNED, NOT CHOSEN (SPEC-153): nobody creates, names or picks one, and there is no
  // room list to offer. Which of the two calls is right is the only real decision on this page,
  // and what separates them is who you end up standing with.
  //
  // With a diagram named, `POST /api/diagrams/{id}/room` returns the room that is *already*
  // showing it, creating one only when there is none. That is what makes two people who follow
  // the same link land in the same room looking at the same thing — which is the entire point of
  // sending somebody a link. Spawning a fresh lobby and then opening the diagram inside it would
  // look identical to whoever did it and leave them alone in a room of their own.
  //
  // With no diagram named, the lobby: a room showing nothing, which is where the diagrams
  // themselves are, standing around you as live miniatures (SPEC-156).
  const room =
    requestedDiagramId === null
      ? await enterLobbyRoom()
      : await enterDiagramRoom(requestedDiagramId);

  // The room id rides in the query string exactly as it did when this was a route, because
  // `mountRoomRoute` reads it back off `window.location.search` and nothing about that changed
  // in the move. `replaceState`, not `pushState`: this is not a navigation the back button
  // should be able to undo — there is no earlier page on this host to go back to.
  const url = new URL(window.location.href);
  url.searchParams.set('roomId', room.id);
  window.history.replaceState(null, '', url);

  const dispose = await mountRoomRoute({
    canvas,
    statusHost,
    splashHost,
    sessionHost,
    hint,
    reenterButton,
    leaveRoomButton,
    account,
    devHandle,
    navigate: leaveToWeb,
    showNotice,
    showFatalError,
  });

  // Held so a teardown is possible at all — nothing on this host unmounts the room today, since
  // leaving means leaving the origin. Kept on the dev handle rather than dropped on the floor,
  // because "the disposer exists and is reachable" is what makes the eventual diagram-swap work
  // (plan/diagram-as-the-product.md: the scene swaps its contents rather than rebuilding) a
  // change to this file rather than a change to `mountRoomRoute`'s contract.
  devHandle.disposeRoom = dispose;
}

void start().catch(showFatalError);
